'use client';

import { useEffect, useState } from 'react';
import type { FieldValues, UseFormWatch } from 'react-hook-form';

import useBoolean from './useBoolean';

type UseFormPersistOptions<T extends FieldValues> = {
  key: string;
  watch: UseFormWatch<T>;
  isDirty: boolean;
};

function useFormPersist<T extends FieldValues>({
  key,
  watch,
  isDirty,
}: UseFormPersistOptions<T>) {
  // Initialize from sessionStorage lazily
  const [cachedValues, setCachedValues] = useState<T | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    const storedData = sessionStorage.getItem(key);
    if (storedData !== null) {
      try {
        return JSON.parse(storedData) as T;
      } catch {
        return null;
      }
    }
    return null;
  });

  const canRestoreCache = useBoolean(cachedValues !== null);

  const resetCache = (): void => {
    sessionStorage.removeItem(key);
    setCachedValues(null);
    canRestoreCache.onFalse();
  };

  useEffect(() => {
    if (isDirty === true) {
      const subscription = watch((values) => {
        sessionStorage.setItem(key, JSON.stringify(values));
      });

      return () => {
        subscription.unsubscribe();
      };
    }

    return undefined;
  }, [isDirty, key, watch]);

  return {
    canRestoreCache: canRestoreCache.value,
    resetCache,
    cachedValues,
  };
}

export default useFormPersist;
